import { Logger, ApiErrorHandler } from '../../utils';

export interface SurveySubmissionPayload {
  user_id: string;
  survey_id: string;
  response_data: Record<string, any>;
  completed_at: string;
  status: 'COMPLETED';
  is_anonymous?: boolean;
  device_id?: string;
  client_ip?: string;
  email_address?: string;
  source?: string;
  rate_limit_info?: {
    ip: string;
    device_id: string;
    submission_count: number;
    first_submission_time?: string;
  };
}

export class SurveyApiService {
  private static readonly SUBMISSION_ENDPOINT = '/.netlify/functions/survey-submit';
  private static readonly DIRECT_API_ENDPOINT = '/api/v1/survey/public/submit-response';


  static async submitSurveyResponse(payload: SurveySubmissionPayload): Promise<any> {
    const isDevelopment = import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.DEV;
    const useDirectApi = isDevelopment && import.meta.env.VITE_USE_DIRECT_API === 'true';

    try {
      if (useDirectApi) {
        Logger.log('📤 Using direct backend API (local development)');
        return await this.submitSurveyResponseDirect(payload);
      } else {
        Logger.log('📤 Using secure API proxy (Netlify Functions)');
        return await this.submitSurveyResponseViaNetlify(payload);
      }
    } catch (error) {
      Logger.error('Failed to submit survey response:', error);
      throw error;
    }
  }

  private static async submitSurveyResponseViaNetlify(payload: SurveySubmissionPayload): Promise<any> {
    const url = new URL(this.SUBMISSION_ENDPOINT, window.location.origin);

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      await ApiErrorHandler.handleApiError(response, 'Survey Submission', url.toString());
    }

    const data = await response.json();
    Logger.log('✅ Survey submission successful');
    return data;
  }

  private static async submitSurveyResponseDirect(payload: SurveySubmissionPayload): Promise<any> {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL_DEV || import.meta.env.VITE_API_BASE_URL_PROD || 'http://localhost:8080';
    const apiAuthToken = import.meta.env.VITE_API_AUTH_TOKEN;

    const url = new URL(this.DIRECT_API_ENDPOINT, apiBaseUrl);

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (apiAuthToken) {
      headers['Authorization'] = `Bearer ${apiAuthToken}`;
    }

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      await ApiErrorHandler.handleApiError(response, 'Survey Submission', url.toString());
    }

    const data = await response.json();
    Logger.log('✅ Survey submission successful (direct)');
    return data;
  }


  static buildSubmissionPayload(
    userId: string,
    surveyId: string,
    answers: Record<string, string | string[] | null>,
    deviceId?: string,
    isAnonymous?: boolean,
    clientIp?: string,
    emailAddress?: string,
    source?: string,
    rateLimitInfo?: {
      ip: string;
      device_id: string;
      submission_count: number;
      first_submission_time?: string;
    }
  ): SurveySubmissionPayload {
    // Filter out null values (unanswered questions) before sending to API
    const filteredAnswers: Record<string, string | string[]> = {};
    Object.entries(answers).forEach(([key, value]) => {
      if (value !== null) {
        filteredAnswers[key] = value;
      }
    });
    
    const payload: SurveySubmissionPayload = {
      user_id: userId,
      survey_id: surveyId,
      response_data: filteredAnswers,
      completed_at: new Date().toISOString(),
      status: 'COMPLETED'
    };
    
    if (deviceId) {
      payload.device_id = deviceId;
    }
    
    if (isAnonymous) {
      payload.is_anonymous = true;
    }

    if (clientIp) {
      payload.client_ip = clientIp;
    }

    if (emailAddress) {
      payload.email_address = emailAddress;
    }

    if (source) {
      payload.source = source;
    }

    if (rateLimitInfo) {
      payload.rate_limit_info = rateLimitInfo;
    }
    
    return payload;
  }
}
